/**
 * PATH: packages/job-hub/src/jobs/qa/QaAssigneeChip.tsx
 * FILE_ID: JOBHUB-QA-ASSIGNEE-CHIP-001
 * PURPOSE: Person-icon assignee popup for QA areas and Run-mode items.
 * LANDMINE: Must be a sibling of AreaRow's select button and stop
 *           propagation so the rail click does not fire. The panel must
 *           portal through AdaptiveOverlay: an in-tree absolute popup is
 *           clipped by QA_PANE_SCROLL_CLASS (overflow-y-auto) and paints
 *           under the selected row.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import { User, UserRound, X } from 'lucide-react'
import { AdaptiveOverlay } from '@quotech/ui'
import type { QaAssigneeCandidate } from './qaTypes'

export function QaAssigneeChip({
  chipId,
  assignedTo,
  assignedName,
  assignedEmail,
  candidates,
  canAssign,
  openId,
  onOpenId,
  onAssign,
  searchCandidates,
}: {
  chipId: string
  assignedTo?: number | null
  assignedName?: string | null
  assignedEmail?: string | null
  candidates: QaAssigneeCandidate[]
  canAssign: boolean
  openId: string | null
  onOpenId: (id: string | null) => void
  onAssign: (userId: number | null) => void
  searchCandidates?: (q: string) => Promise<QaAssigneeCandidate[]>
}) {
  const open = openId === chipId
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [search, setSearch] = useState('')
  const [serverHits, setServerHits] = useState<QaAssigneeCandidate[]>([])
  const [serverSettled, setServerSettled] = useState(false)
  const assigned = assignedTo != null
  const label = assignedName || assignedEmail || 'Unassigned'
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    if (!needle) return candidates
    return candidates.filter((person) => {
      const name = (person.name || '').toLowerCase()
      const email = (person.email || '').toLowerCase()
      return name.includes(needle) || email.includes(needle)
    })
  }, [candidates, search])
  useEffect(() => {
    const q = search.trim()
    if (!open || !searchCandidates || q.length < 1) {
      setServerHits([])
      setServerSettled(false)
      return
    }
    setServerSettled(false)
    let cancelled = false
    const handle = window.setTimeout(() => {
      void searchCandidates(q).then((rows) => {
        if (cancelled) return
        setServerHits(rows)
        setServerSettled(true)
      }).catch(() => {
        if (cancelled) return
        setServerHits([])
        setServerSettled(true)
      })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(handle)
    }
  }, [open, search, searchCandidates])
  const merged = useMemo(() => {
    const byId = new Map<number, QaAssigneeCandidate>()
    for (const person of filtered) byId.set(person.user_id, person)
    for (const person of serverHits) {
      if (person?.user_id && !byId.has(person.user_id)) byId.set(person.user_id, person)
    }
    return [...byId.values()]
  }, [filtered, serverHits])

  return (
    <div className="relative shrink-0" onClick={(event) => event.stopPropagation()}>
      <button
        ref={triggerRef}
        type="button"
        data-testid={`qa-assignee-chip-${chipId}`}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label={assigned ? `Assigned to ${label}` : 'Assign person'}
        title={assigned ? label : 'Assign person'}
        onClick={() => onOpenId(open ? null : chipId)}
        className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
      >
        {assigned
          ? <UserRound className="h-3.5 w-3.5 fill-current" aria-hidden />
          : <User className="h-3.5 w-3.5" aria-hidden />}
      </button>
      <AdaptiveOverlay
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            onOpenId(null)
            setSearch('')
          }
        }}
        anchorRef={triggerRef}
        mode="popover"
        label="Assignee"
        testId={`qa-assignee-popup-${chipId}`}
        popoverWidth={280}
        heightHint={280}
        className="opacity-100"
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-foreground">{label}</p>
            {assignedEmail && (
              <p className="truncate text-[11px] text-muted-foreground">{assignedEmail}</p>
            )}
          </div>
          {canAssign && assigned && (
            <button
              type="button"
              aria-label="Clear assignee"
              data-testid={`qa-assignee-clear-${chipId}`}
              onClick={() => { onAssign(null); onOpenId(null); setSearch('') }}
              className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" aria-hidden />
            </button>
          )}
        </div>
        {canAssign ? (
          <div className="mt-2 border-t border-border pt-2">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search people"
              aria-label="Search people to assign"
              data-testid={`qa-assignee-search-${chipId}`}
              className="mb-1 w-full rounded-md border border-border bg-muted px-2 py-1.5 text-xs text-foreground"
            />
            <ul className="max-h-40 overflow-auto">
              {merged.map((person) => (
                <li key={person.user_id}>
                  <button
                    type="button"
                    data-testid={`qa-assignee-pick-${chipId}-${person.user_id}`}
                    onClick={() => { onAssign(person.user_id); onOpenId(null); setSearch('') }}
                    className="flex w-full flex-col rounded-md px-2 py-1.5 text-left hover:bg-accent"
                  >
                    <span className="truncate text-xs text-foreground">{person.name || person.email}</span>
                    <span className="truncate text-[11px] text-muted-foreground">{person.email}</span>
                  </button>
                </li>
              ))}
              {merged.length === 0 && (
                <li className="px-2 py-1.5 text-[11px] text-muted-foreground">
                  {search.trim().length > 0 && searchCandidates
                    ? (serverSettled ? 'No people' : '')
                    : candidates.length === 0
                      ? 'They need an organisation login before you can assign them. Invite them from the Teams tab.'
                      : 'No one matches that search.'}
                </li>
              )}
            </ul>
          </div>
        ) : (
          <p className="mt-2 border-t border-border pt-2 text-[11px] text-muted-foreground" data-testid={`qa-assignee-locked-${chipId}`}>
            You cannot assign people on this job. An owner can change Can assign tasks on the Teams tab.
          </p>
        )}
      </AdaptiveOverlay>
    </div>
  )
}
